import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type {
  IRebillsWrap,
  IReduxRequestNode,
  IStandardGUJRequestParams,
} from "@litres-libs/types";
import { Request } from "../../request/Request";
import { addHydrateReducer } from "../../utils/hydrateReducer";

interface IPaymentMethodsState {
  list: IRebillsWrap;
  actions: {
    deletePaymentCardCSRF: TDeletePaymentCardCSRFNode;
    getRebillsGUJ: TgetRebillsGUJNode;
  };
}
type TDeletePaymentCardCSRFNode = IReduxRequestNode<
  IDeletePaymentCardCSRFRequest,
  IDeletePaymentCardGUJResponse
>;
type TgetRebillsGUJNode = IReduxRequestNode<
  IGetRebillsGUJRequest,
  IGetRebillsGUJResponse
>;

const initialState: IPaymentMethodsState = {
  list: {},
  actions: {
    deletePaymentCardCSRF: {},
    getRebillsGUJ: {},
  },
};

interface IGetRebillsGUJRequest {}
interface IGetRebillsGUJResponse {
  rebills: IRebillsWrap;
}

export const getRebillsGUJ = createAsyncThunk(
  "user/rebills/getRebillsGUJ",
  async () => {
    const response = new Request.CSRF<
      IGetRebillsGUJRequest,
      IGetRebillsGUJResponse
    >({
      url: "/pages/personal_cabinet_payment_methods/?lite=1&gu_ajax=1",
      provider: {
        id: "get_rebills",
      },
      request: {
        method: "GET",
      },
    });

    return await response.fetch();
  }
);

interface IPaymentCardDeleteParams {
  cardnum?: string;
  card_num?: string;
  mode?: number;
  descr?: string;
  rebil?: number;
}
type IDeletePaymentCardCSRFRequest = IStandardGUJRequestParams &
  IPaymentCardDeleteParams;
interface IDeletePaymentCardGUJResponse {
  status: string;
}

export const deletePaymentCardCSRF = createAsyncThunk(
  "user/rebills/deletePaymentCardCSRF",
  async (params: {
    // Тип экшена. Для разных ребилов используются разные экшены
    actionType: "do_del_token";
    data: IPaymentCardDeleteParams;
    onSuccess?: (success: TDeletePaymentCardCSRFNode) => void;
    onError?: (error: TDeletePaymentCardCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      IDeletePaymentCardCSRFRequest,
      IDeletePaymentCardGUJResponse
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "delete_payment_card",
      },
      request: {
        method: "POST",
        data: {
          action: params.actionType,
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

export const rebillsSlice = createSlice({
  name: "user/rebills",
  initialState,
  reducers: {
    // Сеттеров нет
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "user.rebills");
    /**
     * Обработка загрузки списка карт paymentMethodsCSRF
     */
    builder.addCase(getRebillsGUJ.pending, (state: IPaymentMethodsState) => {
      state.actions.getRebillsGUJ.isLoading = true;
      state.actions.getRebillsGUJ.error = undefined;
    });
    builder.addCase(
      getRebillsGUJ.fulfilled,
      (
        state: IPaymentMethodsState,
        action: PayloadAction<TgetRebillsGUJNode>
      ) => {
        // state.actions.getRebillsGUJ = action.payload;
        state.actions.getRebillsGUJ.isLoading = false;

        if (action.payload.response?.rebills) {
          state.list = action.payload.response.rebills;
        }

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          console.error(
            "Ошибка запроса paymentMethodsCSRF",
            action.payload.error
          );
          state.actions.getRebillsGUJ.error = action.payload.error;
        }
      }
    );

    /**
     * Обработка загрузки и ответа от deletePaymentCardCSRF
     */
    builder.addCase(
      deletePaymentCardCSRF.pending,
      (state: IPaymentMethodsState) => {
        state.actions.deletePaymentCardCSRF.isLoading = true;
      }
    );
    builder.addCase(
      deletePaymentCardCSRF.fulfilled,
      (
        state: IPaymentMethodsState,
        action: PayloadAction<TDeletePaymentCardCSRFNode>
      ) => {
        state.actions.deletePaymentCardCSRF = action.payload;
        state.actions.deletePaymentCardCSRF.isLoading = false;

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          console.error(
            "Ошибка запроса addItemRatingCSRF",
            action.payload.error
          );
          state.actions.deletePaymentCardCSRF.error = action.payload.error;
        }
      }
    );
  },
});

export default rebillsSlice.reducer;
